import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SeverityBadge, StatusBadge } from "@/components/Primitives";
import { Server, Cpu, MemoryStick, HardDrive, ShieldCheck, Activity, Ticket } from "lucide-react";

function Spec({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 py-2"><Icon size={14} className="text-zinc-500" />
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono w-28">{label}</div>
      <div className="text-sm text-zinc-200 font-mono">{value}</div>
    </div>
  );
}

export default function AssetDetail() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  useEffect(() => { api.get(`/assets/${id}`).then(r => setA(r.data)); }, [id]);
  if (!a) return <div className="text-zinc-500 text-sm">Loading…</div>;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <div className="xl:col-span-1 space-y-4">
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
          <div className="flex items-center gap-2 mb-3"><Server size={16} className="text-blue-400" /><div className="text-sm text-zinc-500 uppercase font-mono tracking-widest">{a.category}</div></div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">{a.hostname}</h1>
          <div className="text-xs text-zinc-500 mt-1 font-mono">{a.serial_number} · {a.asset_tag}</div>
          <div className="mt-4"><StatusBadge status={a.status} /></div>
          <div className="mt-5 divide-y divide-zinc-900">
            <Spec icon={Cpu} label="CPU" value={a.cpu} />
            <Spec icon={MemoryStick} label="RAM" value={`${a.ram_gb} GB`} />
            <Spec icon={HardDrive} label="Disk" value={`${a.disk_gb} GB`} />
            <Spec icon={Server} label="Manufacturer" value={`${a.manufacturer} ${a.model}`} />
            <Spec icon={Server} label="OS" value={`${a.os} ${a.os_version}`} />
            <Spec icon={Activity} label="IP" value={a.ip_address} />
            <Spec icon={Activity} label="MAC" value={a.mac_address} />
            <Spec icon={Activity} label="Uptime" value={`${a.uptime_hours}h`} />
          </div>
        </div>
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-3">Security Posture</div>
          <div className="grid grid-cols-2 gap-2">
            {[["AV", a.av_status],["Firewall", a.firewall_status],["BitLocker", a.bitlocker_status],["Defender", a.defender_status],["Secure Boot", a.secure_boot]].map(([k,v]) => (
              <div key={k} className="bg-zinc-900/50 border border-zinc-900 rounded p-2">
                <div className="text-[10px] text-zinc-500 uppercase">{k}</div>
                <div className={`text-xs font-mono ${v==="enabled"?"text-emerald-400":"text-red-400"}`}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="xl:col-span-2 space-y-4">
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-medium">Machine Timeline</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest font-mono">{a.timeline?.length || 0} events</div>
          </div>
          <div className="relative pl-6 border-l-2 border-zinc-900 space-y-5 max-h-[600px] overflow-y-auto pr-2" data-testid="machine-timeline">
            {(a.timeline || []).map(ev => (
              <div key={ev.id} className="relative">
                <span className={`timeline-dot absolute -left-[31px] top-1 w-3 h-3 rounded-full ${ev.severity==="critical"?"bg-red-500":ev.severity==="high"?"bg-orange-500":ev.severity==="medium"?"bg-amber-500":ev.severity==="low"?"bg-emerald-500":"bg-blue-500"}`} />
                <div className="font-mono text-[11px] text-zinc-500">{ev.at?.slice(0,19).replace("T"," ")}</div>
                <div className="bg-zinc-900 border border-zinc-800 rounded-md p-3 mt-1">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium">{ev.title}</div>
                    <SeverityBadge level={ev.severity}>{ev.event_type}</SeverityBadge>
                  </div>
                  {ev.description && <div className="text-xs text-zinc-400 mt-1">{ev.description}</div>}
                </div>
              </div>
            ))}
            {(a.timeline || []).length === 0 && <div className="text-zinc-500 text-sm">No events yet.</div>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
            <div className="flex items-center gap-2 mb-3"><Ticket size={14} /><div className="font-display font-medium">Linked Tickets ({a.tickets?.length || 0})</div></div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {(a.tickets || []).slice(0, 20).map(t => (
                <Link to={`/tickets/${t.id}`} key={t.id} className="block border border-zinc-900 rounded p-2 hover:border-zinc-700">
                  <div className="flex items-center gap-2"><SeverityBadge level={t.priority}>{t.priority}</SeverityBadge><div className="text-xs">{t.title}</div></div>
                  <div className="text-[10px] text-zinc-500 font-mono mt-1">#{t.number} · {t.category} · {t.status}</div>
                </Link>
              ))}
              {(a.tickets || []).length === 0 && <div className="text-zinc-500 text-sm">No tickets.</div>}
            </div>
          </div>
          <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
            <div className="flex items-center gap-2 mb-3"><ShieldCheck size={14} /><div className="font-display font-medium">Findings</div></div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Compliance ({a.compliance_findings?.length || 0})</div>
            <div className="space-y-1 mb-3">
              {(a.compliance_findings || []).slice(0,5).map(f => (
                <div key={f.id} className="flex items-center gap-2 text-xs"><SeverityBadge level={f.severity} /><div>{f.title}</div></div>
              ))}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Security ({a.security_findings?.length || 0})</div>
            <div className="space-y-1">
              {(a.security_findings || []).slice(0,5).map(f => (
                <div key={f.id} className="flex items-center gap-2 text-xs"><SeverityBadge level={f.severity} /><div>{f.title}</div></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
